import { Link, useLocation } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  Bell,
  Blocks,
  Building2,
  CreditCard,
  Puzzle,
  Shield,
  ShieldCheck,
  User,
  Users,
} from "lucide-react";
import { SidebarResizeHandle } from "@/components/sidebar-resize-handle";
import { SidebarUserProfile } from "@/components/sidebar-user-profile";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { useSettingsOrigin } from "@/features/settings/settings-origin-context";
import { cn } from "@/lib/utils";

type SettingsNavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
};

const personalItems: SettingsNavItem[] = [
  { label: "Preferences", to: "/settings/profile", icon: User },
  { label: "Security", to: "/settings/security", icon: Shield },
  { label: "Notifications", to: "/settings/notifications", icon: Bell },
  { label: "API & Integrations", to: "/settings/api-integrations", icon: Puzzle },
];

const organizationItems: SettingsNavItem[] = [
  { label: "General", to: "/settings/general", icon: Building2 },
  { label: "Members & Roles", to: "/settings/members-roles", icon: Users },
  { label: "Quality Guard", to: "/settings/quality-guard", icon: ShieldCheck },
  { label: "Billing", to: "/settings/billing", icon: CreditCard },
  { label: "Integrations", to: "/settings/integrations", icon: Blocks },
];

function SettingsSection({ title, items }: { title: string; items: SettingsNavItem[] }) {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <div className="flex flex-col gap-px">
      <div className="flex h-7 select-none items-center px-1 text-xs font-medium text-muted-foreground">
        {title}
      </div>
      {items.map((item) => {
        const isActive = location.pathname === item.to;
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => {
              if (isMobile) {
                setOpenMobile(false);
              }
            }}
            className={cn(
              "flex h-7 select-none items-center gap-1.5 border-l-2 border-transparent pl-5 text-xs transition-colors touch:min-h-11",
              isActive
                ? "border-primary bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            )}
          >
            <Icon
              className={cn("size-3 shrink-0", isActive ? "text-primary" : "text-neutral-dim")}
            />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function SettingsSidebar() {
  const { goBackFromSettings } = useSettingsOrigin();
  const { isMobile, setOpenMobile } = useSidebar();

  const handleBack = () => {
    goBackFromSettings();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar
      side="left"
      collapsible="offcanvas"
      className="bg-sidebar"
      data-testid="settings-sidebar"
      aria-label="Settings navigation"
    >
      <SidebarHeader className="gap-0 p-0 px-4">
        <button
          type="button"
          onClick={handleBack}
          className="flex h-12 select-none items-center gap-2 text-left"
        >
          <ArrowLeft className="size-3.5 text-neutral-dim" />
          <span className="text-sm font-semibold text-foreground">Settings</span>
        </button>
        <div className="h-px bg-border -mx-4" />
      </SidebarHeader>

      <SidebarContent className="px-4">
        <div className="flex flex-col gap-0.5">
          <SettingsSection title="Personal" items={personalItems} />
          <div className="my-0.5 h-px bg-border" />
          <SettingsSection title="Organization" items={organizationItems} />
        </div>
      </SidebarContent>

      <SidebarFooter className="gap-0 p-0">
        <div className="mx-4 h-px bg-border" />
      </SidebarFooter>
      <SidebarUserProfile />
      <SidebarResizeHandle />
    </Sidebar>
  );
}
